import { Box, Button, Typography, Grid } from '@mui/material';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { Colors } from '../themes';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.name === 'ChunkLoadError' ||
      /Loading chunk .* failed/.test(error.message) ||
      /Failed to fetch dynamically imported module/.test(error.message)
    );
  }
  return false;
}

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const { t } = useTranslation();

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (isChunkLoadError(error)) {
      const reloadedKey = 'chunk_reload';
      if (!sessionStorage.getItem(reloadedKey)) {
        sessionStorage.setItem(reloadedKey, '1');
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(reloadedKey);
    }
  }, [error]);

  useEffect(() => {
    if (is404) return;

    import('@embrace-io/web-sdk')
      .then(({ log }) => {
        log.logException(error, {
          handled: true,
          handler: 'manual',
          attributes: {
            'error.boundary': 'RouteErrorBoundary',
            'error.url': window.location.href,
          },
        });
      })
      .catch(() => {});
  }, [error, is404]);

  const handleGoHome = () => {
    window.location.assign('/');
  };

  return (
    <Grid
      container
      justifyContent='center'
      alignItems='center'
      direction='column'
      sx={{ minHeight: '100vh', textAlign: 'center', px: 3 }}>
      <Box sx={{ maxWidth: 480 }}>
        <Typography variant='h1' color={Colors.primaryBase} gutterBottom>
          {is404 ? t('PageNotFound') : t('SomethingWentWrong')}
        </Typography>
        <Typography variant='p16' color={Colors.secondaryBase} mt={2}>
          {is404
            ? t('ThePageYouAreLookingForDoesNotExist')
            : t('AnUnexpectedErrorOccurredPleaseTryAgain')}
        </Typography>
        <Grid container justifyContent='center' mt={4}>
          <Button onClick={handleGoHome}>{t('GoHome')}</Button>
        </Grid>
      </Box>
    </Grid>
  );
};

export default RouteErrorBoundary;
